import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

export interface ReminderStage {
  offset_days: number;
  tone: 'friendly' | 'firm';
}

export interface FlatFeeSetting {
  enabled: boolean;
  price: number;
}

export interface SeaSsbtSetting {
  enabled: boolean;
  cities: string[];
  prices: { bib_3g: number; bib_5g: number; case_24: number };
}

export interface OrderFees {
  /** Delivery fuel surcharge — every order. */
  fuel: FlatFeeSetting;
  /** DOT hazmat fee — once per order with a gas cylinder. */
  hazmat: FlatFeeSetting;
  /** Force majeure fee — once per order with a gas cylinder. */
  force_majeure: FlatFeeSetting;
  /** CA Redemption Value — per 24-pack ($1.20 = 24 × $0.05/can). */
  crv: FlatFeeSetting;
  /** Washington State syrup tax — per 3-gal BIB, WA delivery locations only.
   *  Optional: rows saved before 2026-10-20 do not carry it. */
  wa_syrup?: FlatFeeSetting;
  /** Seattle sweetened beverage tax — per container, delivery city in `cities`
   *  only. Optional: rows saved before 2026-10-23 do not carry it. */
  sea_ssbt?: SeaSsbtSetting;
  /** The portal charges the fees on the SF job (SF's automatic fees must be off). */
  send_to_sf?: boolean;
  /**
   * SSB beverage tax — price is per-ZBT-item (set in QBO, not here).
   * cities/zips are the admin-managed taxed geography; when absent the
   * server falls back to the SF/Oakland/Berkeley defaults. Each zips entry
   * is "94110" or an inclusive range "94102-94188".
   */
  ssb: { enabled: boolean; cities?: string[]; zips?: string[] };
}

/**
 * Order Desk — the staff-forwarded email order channel. NULL / enabled=false
 * means the whole channel is off and the inbound webhook ignores the address.
 */
export interface OrderDeskConfig {
  enabled: boolean;
  /** The inbound mailbox staff forward customer emails to. */
  inbound_address: string | null;
  /** Domains or full addresses allowed to forward. STAFF ONLY. */
  staff_allowlist: string[];
  /** Who gets the Accept / Open-to-edit / Not-an-order email. */
  review_emails: string[];
  copy_to: string | null;
  extraction_hints: string | null;
}

/** off = as before (caller ID attaches) · email = emailed code · sms = texted code, email fallback. */
export type VoiceVerificationMode = 'off' | 'email' | 'sms';

export interface CompanySettings {
  company_name: string;
  from_name: string;
  remittance_email: string | null;
  accounting_email: string | null;
  reply_to: string | null;
  credit_request_recipients: string[];
  statement_send_day: number;
  reminders_enabled_global: boolean;
  statements_enabled_global: boolean;
  reminder_schedule: ReminderStage[];
  order_fees: OrderFees;
  order_desk: OrderDeskConfig | null;
  /** Phone agent caller verification — read by voice-brain per call. */
  voice_verification_mode: VoiceVerificationMode;
  updated_at: string;
  updated_by: string | null;
}

/** Fields a superadmin may PUT. */
export type CompanySettingsPatch = Partial<
  Pick<
    CompanySettings,
    | 'company_name'
    | 'from_name'
    | 'remittance_email'
    | 'accounting_email'
    | 'reply_to'
    | 'credit_request_recipients'
    | 'statement_send_day'
    | 'reminders_enabled_global'
    | 'statements_enabled_global'
    | 'reminder_schedule'
    | 'order_fees'
    | 'order_desk'
    | 'voice_verification_mode'
  >
>;

/**
 * Which inbound signing secrets the FUNCTIONS environment actually holds.
 * Booleans only — the values never leave the server. Reported here because the
 * admin page has no other way to see Netlify env, and probing the intake can
 * only prove that *some* secret is set.
 */
export interface CompanySettingsEnv {
  order_desk_secret: boolean;
  inbound_secret_fallback: boolean;
}

export function useCompanySettings() {
  return useQuery<CompanySettings & { _env?: CompanySettingsEnv }>({
    queryKey: ['admin', 'company-settings'],
    queryFn: async () => {
      const res = await adminFetch<{
        ok: true;
        settings: CompanySettings;
        env?: CompanySettingsEnv;
      }>('admin-company-settings');
      return { ...res.settings, _env: res.env };
    },
    staleTime: 60 * 1000,
  });
}

export function useUpdateCompanySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: CompanySettingsPatch) =>
      adminFetch<{ ok: true; settings: CompanySettings }>('admin-company-settings', {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'company-settings'] });
    },
  });
}
